import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { toDemographicCounts } from '../demographics';
import type { BlackTechStreetData } from './types';
import { MAX_QUARTERS_CHART } from '../constants';

const asNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export function mapApiToBlackTechStreetData(
  data: DashboardMetricsResponse | undefined,
): BlackTechStreetData | null {
  if (!data) return null;
  const section = data.sections.find((entry) => entry.sectionId === 'gace');
  if (!section) return null;

  const byId = new Map(section.metrics.map((metric) => [metric.metricId, metric]));
  const participants = byId.get('gace.participants');
  const participantsByRace = byId.get('gace.participantsByRace');
  const events = byId.get('gace.events');
  const educationOutcomes = byId.get('gace.educationOutcomes');
  const engagements = byId.get('gace.engagements');

  if (!participants || !participantsByRace || !events || !educationOutcomes || !engagements) {
    return null;
  }

  return {
    participants: participants.series.slice(-MAX_QUARTERS_CHART).map((point) => ({
      quarter: String(point.quarter),
      participantsStarted: asNumber(point.participantsStarted),
      qoq: (point.qoq ?? {}) as Record<string, unknown>,
      yoy: (point.yoy ?? {}) as Record<string, unknown>,
    })),
    participantsAllTimeTotal: asNumber(participants.allTime?.participantsStarted),
    race: participantsByRace.series.slice(-MAX_QUARTERS_CHART).map((point) => {
      return {
        quarter: String(point.quarter),
        byRace: toDemographicCounts(point.byRace),
        qoq: (point.qoq ?? {}) as Record<string, unknown>,
        yoy: (point.yoy ?? {}) as Record<string, unknown>,
      };
    }),
    raceAllTimeByRace: toDemographicCounts(participantsByRace.allTime?.byRace),
    events: events.series.slice(-MAX_QUARTERS_CHART).map((point) => ({
      quarter: String(point.quarter),
      communityEngagementTotal: asNumber(point.communityEngagementTotal),
      communityEngagementLarge: asNumber(point.communityEngagementLarge),
      communityEngagementSmall: asNumber(point.communityEngagementSmall),
      educationTotal: asNumber(point.educationTotal),
      educationLarge: asNumber(point.educationLarge),
      educationSmall: asNumber(point.educationSmall),
      qoq: (point.qoq ?? {}) as Record<string, unknown>,
      yoy: (point.yoy ?? {}) as Record<string, unknown>,
    })),
    eventsAllTime: {
      communityEngagementTotal: asNumber(events.allTime?.communityEngagementTotal),
      communityEngagementLarge: asNumber(events.allTime?.communityEngagementLarge),
      educationTotal: asNumber(events.allTime?.educationTotal),
      educationLarge: asNumber(events.allTime?.educationLarge),
      educationSmall: asNumber(events.allTime?.educationSmall),
    },
    educationOutcomes: educationOutcomes.series.slice(-MAX_QUARTERS_CHART).map((point) => ({
      quarter: String(point.quarter),
      certifications: asNumber(point.certifications),
      aiFluency: asNumber(point.aiFluency),
      digitalLiteracy: asNumber(point.digitalLiteracy),
      digitalExposure: asNumber(point.digitalExposure),
      qoq: (point.qoq ?? {}) as Record<string, unknown>,
      yoy: (point.yoy ?? {}) as Record<string, unknown>,
    })),
    engagements: engagements.series.slice(-MAX_QUARTERS_CHART).map((point) => ({
      quarter: String(point.quarter),
      labResources: asNumber(point.labResources),
      startups: asNumber(point.startups),
      productsDeveloped: asNumber(point.productsDeveloped),
      productsCommercialized: asNumber(point.productsCommercialized),
      productsScaled: asNumber(point.productsScaled),
      qoq: (point.qoq ?? {}) as Record<string, unknown>,
      yoy: (point.yoy ?? {}) as Record<string, unknown>,
    })),
  };
}
